import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';

export const useProducts = (filters, pagination) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  // Global statistics (not affected by filters/pagination)
  const [globalStats, setGlobalStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    lowStock: 0
  });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category_id', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      if (filters.lowStock) params.append('low_stock', 'true');
      params.append('sort', filters.sortBy || 'created_at');
      params.append('order', filters.sortOrder || 'desc');
      params.append('page', pagination.currentPage);
      params.append('limit', pagination.itemsPerPage);

      const response = await axios.get(`/api/products?${params.toString()}`);
      
      setProducts(response.data.products || []);
      setPaginationData(response.data.pagination || paginationData);
      
      // Update global stats if returned from API
      if (response.data.globalStats) {
        setGlobalStats(response.data.globalStats);
      }
    } catch (err) {
      setError(err.message);
      setProducts([]);
      toast.error('שגיאה בטעינת המוצרים');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const updateProduct = useCallback(async (productId, updates) => {
    try {
      const response = await axios.put(`/api/products/${productId}`, updates);
      
      // Optimistic update
      setProducts(prevProducts =>
        prevProducts.map(product =>
          product.id === productId ? { ...product, ...response.data.product } : product
        )
      );
      
      toast.success('המוצר עודכן בהצלחה');
      return response.data.product;
    } catch (err) {
      toast.error(err.response?.data?.message || 'שגיאה בעדכון המוצר');
      throw err;
    }
  }, []);

  const deleteProduct = useCallback((productId) => {
    // Just update local state - the actual deletion happens in DeleteProductConfirmation
      setProducts(prevProducts =>
        prevProducts.filter(product => product.id !== productId)
      );
      setPaginationData(prev => ({
        ...prev,
        totalItems: prev.totalItems - 1
      }));
  }, []);

  const addProduct = useCallback((newProduct) => {
    setProducts(prevProducts => [newProduct, ...prevProducts]);
    setPaginationData(prev => ({
      ...prev,
      totalItems: prev.totalItems + 1
    }));
  }, []);

  return {
    products,
    loading,
    error,
    pagination: paginationData,
    globalStats,
    refetch: fetchProducts,
    updateProduct,
    deleteProduct,
    addProduct
  };
};

